/* Native gift eligibility belongs to Shopify BXGY. The theme quotes before adding,
   and removes only its own gifts if their conditions disappear in the controlled cart. */
(() => {
  if (window.K9NativeGift) return;
  const marker = '_k9_cadeau';
  const money = (value, currency) => {
    if (value?.currencyCode !== currency || !/^\d+(?:\.\d{1,2})?$/.test(value.amount)) throw Error('currency');
    const cents = Math.round(Number(value.amount) * 100);
    if (!Number.isSafeInteger(cents)) throw Error('amount');
    return cents;
  };
  const identity = item => JSON.stringify([Number(item.id ?? item.variant_id),
    Object.entries(item.properties || {}).filter(([, value]) => value !== '').sort(([a], [b]) => a.localeCompare(b))]);
  const quantities = items => {
    const result = new Map();
    for (const item of items) {
      if (!Number.isSafeInteger(item.quantity) || item.quantity < 1) throw Error('quantity');
      const key = identity(item);
      result.set(key, (result.get(key) || 0) + item.quantity);
    }
    return result;
  };
  const selected = (config, key, choice) => config.products[key].variants.find(v => String(v.id) === String(choice) && v.available);
  const validateCart = (cart, currency) => {
    if (!Array.isArray(cart?.items) || (currency && cart.currency !== currency)
      || cart.items.some(i => !Number.isSafeInteger(i.quantity) || i.quantity < 1
        || !Number.isSafeInteger(i.product_id) || i.product_id < 1
        || !Number.isSafeInteger(i.id) || i.id < 1
        || !Number.isSafeInteger(i.final_line_price) || i.final_line_price < 0 || !i.key)) throw Error('cart-shape');
    return cart;
  };
  const qualifyingIds = config => {
    const ids = config.qualifyingProductIds === undefined ? [config.products.primary.id] : config.qualifyingProductIds;
    if (!Array.isArray(ids) || !ids.length || ids.length > 250
      || ids.some(id => !Number.isSafeInteger(id) || id < 1)
      || new Set(ids).size !== ids.length || !ids.includes(config.products.primary.id)
      || ids.includes(config.products.secondary.id)) throw Error('qualifying-products');
    return ids;
  };
  const validateRule = config => {
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(config.handle)) throw Error('handle');
    const ids = ['primary', 'secondary'].map(key => config.products[key]?.id);
    if (ids.some(id => !Number.isSafeInteger(id) || id < 1) || ids[0] === ids[1]) throw Error('products');
    qualifyingIds(config);
    for (const key of ['primary', 'secondary']) {
      const variants = config.products[key].variants;
      if (!Array.isArray(variants) || !variants.length || variants.length > 250
        || new Set(variants.map(v => v.id)).size !== variants.length
        || variants.some(v => !Number.isSafeInteger(v.id) || v.id < 1 || !Number.isSafeInteger(v.price) || v.price < 1)) throw Error('variants');
    }
    return config;
  };
  const quota = (cart, rule) => {
    const qualifying = qualifyingIds(rule);
    let buys = 0, gets = 0;
    for (const item of cart.items) {
      if (qualifying.includes(Number(item.product_id))) buys += item.quantity;
      if (Number(item.product_id) === rule.products.secondary.id) gets += item.quantity;
    }
    return Math.max(0, buys - gets);
  };
  const cleanupPlan = (cart, rules) => {
    validateCart(cart);
    const byHandle = new Map(rules.map(rule => [validateRule(rule).handle, rule]));
    if (byHandle.size !== rules.length) throw Error('duplicate-rule');
    // The same gift must share one rule across all qualifying products.
    if (new Set(rules.map(rule => rule.products.secondary.id)).size !== rules.length) throw Error('duplicate-gift');
    const remaining = new Map(), changes = [];
    for (const rule of rules) {
      const qualifying = qualifyingIds(rule);
      const buys = cart.items.filter(i => qualifying.includes(Number(i.product_id))).reduce((sum, i) => sum + i.quantity, 0);
      // Cleanup preserves explicit choices. Ordinary purchases may receive Shopify's
      // free allocation, but never authorize deleting an otherwise entitled K9 gift.
      remaining.set(rule.handle, buys);
    }
    // If the owned quantity exceeds its quota, keep free fragments first,
    // independently of Shopify's line ordering. Never remove ordinary purchases.
    for (const item of [...cart.items].sort((a, b) => Number(a.final_line_price !== 0) - Number(b.final_line_price !== 0))) {
      const handle = item.properties?.[marker];
      if (!handle) continue;
      const rule = byHandle.get(handle);
      let keep = rule && Number(item.product_id) === rule.products.secondary.id
        ? Math.min(item.quantity, remaining.get(handle)) : 0;
      if (rule) remaining.set(handle, remaining.get(handle) - keep);
      if (keep !== item.quantity) changes.push({ id: item.key, quantity: keep });
    }
    return changes;
  };
  const giftBalance = (cart, rule) => {
    const title = { 'piscine-balle-offerte': 'K9 — Piscine + balle offerte',
      'housses-ceinture-offerte': 'K9 — Housses GuardShield + ceinture offerte' }[rule.handle];
    if (!title) throw Error('gift-rule');
    const triggers = cart.items.filter(i => qualifyingIds(rule).includes(i.product_id));
    if (triggers.some(i => i.has_components || i.item_components?.length || i.parent_relationship || i.selling_plan_allocation)) throw Error('gift-line');
    const buys = triggers.reduce((n, i) => n + i.quantity, 0);
    let quantity = 0, free = 0, total = 0, ordinaryCeiling = 0;
    const bands = new Map();
    for (const line of cart.items.filter(i => i.product_id === rule.products.secondary.id)) {
      if (line.has_components || line.item_components?.length || line.parent_relationship || line.selling_plan_allocation) throw Error('gift-line');
      const variant = rule.products.secondary.variants.find(v => v.id === line.id);
      const original = variant?.price * line.quantity;
      if (!variant || !Number.isSafeInteger(line.quantity) || line.quantity < 1
        || line.original_line_price !== original || !Number.isSafeInteger(line.final_line_price) || line.final_line_price < 0) throw Error('gift-price');
      if (line.properties?.[marker] && line.properties[marker] !== rule.handle) throw Error('gift-rule');
      if (!line.properties?.[marker]) ordinaryCeiling += original;
      let gift = 0, discounts = 0;
      for (const allocation of line.line_level_discount_allocations || []) {
        if (!Number.isSafeInteger(allocation.amount) || allocation.amount < 0) throw Error('gift-allocation');
        discounts += allocation.amount;
        if (allocation.discount_application?.title === title && allocation.discount_application.type !== 'discount_code') gift += allocation.amount;
      }
      if (gift % variant.price || gift > original || discounts + line.final_line_price !== original) throw Error('gift-allocation');
      quantity += line.quantity; free += gift / variant.price; total += line.final_line_price;
      const band = bands.get(line.id) || [line.id, 0, 0, 0];
      band[1] += line.quantity; band[2] += gift / variant.price; band[3] += line.final_line_price; bands.set(line.id, band);
    }
    if (free !== Math.min(buys, quantity) || total > ordinaryCeiling) throw Error('gift-not-free');
    return { quantity, free, total, bands: [...bands.values()].sort(([a], [b]) => a - b) };
  };
  const verifyOwned = (cart, rules) => {
    for (const rule of rules) {
      if (cart.items.some(i => i.properties?.[marker] === rule.handle)) giftBalance(cart, rule);
    }
    return true;
  };
  const quoteLines = items => {
    if (items.length > 250 || items.some(i => i.selling_plan_allocation)) throw Error('cart-shape');
    const merged = new Map();
    for (const item of items) {
      const properties = item.properties?.[marker] ? { [marker]: item.properties[marker] } : {};
      const key = identity({ ...item, properties });
      const line = merged.get(key) || { merchandiseId: `gid://shopify/ProductVariant/${item.id ?? item.variant_id}`,
        quantity: 0, attributes: Object.entries(properties).map(([key, value]) => ({ key, value })) };
      line.quantity += item.quantity;
      merged.set(key, line);
    }
    return [...merged.values()];
  };
  const api = { marker, selected, identity, quantities, validateRule, validateCart, quota, cleanupPlan, giftBalance, verifyOwned, quoteLines, checkoutBlocked: false, hasOwned: false };
  window.K9NativeGift = api;

  function register() {
    if (!window.K9NativeOffer || customElements.get('k9-gift-cart')) return;
    const { Base, state } = window.K9NativeOffer;
    const offers = () => [...document.querySelectorAll('k9-patch-offer, k9-product-offer, k9-fixed-offer, k9-gift-offer, k9-bogo-offer, k9-paliers-offer')].filter(e => e.config);
    const activeRules = config => {
      const rules = document.querySelector('k9-gift-cart')?.rules || [];
      return rules.some(rule => rule.handle === config.handle) ? rules : [...rules, config];
    };
    const refreshOffers = () => offers().forEach(e => e.update());
    const checkoutButtons = () => [...document.querySelectorAll('#CartDrawer-Checkout, #checkout, [name="checkout"], [data-k9-gift-checkout]')];
    const blockCheckout = (value, count) => {
      api.checkoutBlocked = value;
      for (const button of checkoutButtons()) button.disabled = value || count === 0;
      for (const express of document.querySelectorAll('[data-k9-gift-express]')) express.hidden = value || count === 0;
    };
    let verificationTimer;
    api.holdMutation = (force = false) => {
      if (!force && !api.hasOwned && !document.querySelector('k9-gift-cart[data-has-owned="true"]')) return;
      blockCheckout(true);
      clearTimeout(verificationTimer);
      // Also covers rejected edits and failed requests that did not redraw a cart section.
      verificationTimer = setTimeout(() => { void document.querySelector('k9-gift-cart')?.reconcile(); }, 700);
    };
    const query = `mutation K9GiftQuote($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {
      cartCreate(input: $input) { cart { id lines(first: 250) { nodes {
        id quantity attributes { key value } merchandise { ... on ProductVariant { id product { id } } }
        cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
        discountAllocations { __typename discountedAmount { amount currencyCode }
          ... on CartAutomaticDiscountAllocation { title } ... on CartCodeDiscountAllocation { code } }
      } pageInfo { hasNextPage } } } userErrors { code } warnings { code } }
    }`;

    const clearQuery = `mutation K9GiftClear($cart: ID!, $lines: [ID!]!, $country: CountryCode!) @inContext(country: $country) {
      cartLinesRemove(cartId: $cart, lineIds: $lines) { cart { totalQuantity } userErrors { code } }
    }`;
    let quoteQueue = Promise.resolve(), quoteUncertain = false;
    const pendingQuotes = new Map();

    const quoteEngine = {
      async storefront(query, variables) {
        const url = new URL(this.config.storefrontUrl, location.origin);
        if (url.origin !== location.origin) throw Error('endpoint');
        const { response, data } = await Base.json(url.href, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query, variables }) });
        if (!response.ok || data.errors?.length) throw Error('storefront');
        return data.data;
      },
      async quote(items, isCurrent = () => true) {
        const request = quoteQueue.then(() => isCurrent() ? quoteEngine.performQuote.call(this, items) : null);
        quoteQueue = request.catch(() => {});
        return request;
      },
      async performQuote(items) {
        if (quoteUncertain || pendingQuotes.size) throw Error('quote-cleanup');
        const lines = quoteLines(items);
        let created;
        try {
          quoteUncertain = true;
          const data = await this.storefront(query, { country: this.config.country, input: { buyerIdentity: { countryCode: this.config.country }, lines } });
          const result = data?.cartCreate;
          const cart = result?.cart;
          if (typeof cart?.id === 'string' && cart.id && Array.isArray(cart.lines?.nodes)
            && cart.lines.nodes.every(line => typeof line.id === 'string' && line.id)) {
            created = { id: cart.id, lines: cart.lines.nodes.map(line => line.id) };
            pendingQuotes.set(created.id, created.lines);
            quoteUncertain = false;
          } else if (cart === null && Array.isArray(result.userErrors) && result.userErrors.length) quoteUncertain = false;
          if (!created) throw Error('quote');
          const actual = result?.cart?.lines;
          if (!actual || actual.pageInfo.hasNextPage || result.userErrors.length || result.warnings.length) throw Error('quote');
          const mapped = actual.nodes.map(line => ({ id: Number(line.merchandise.id.split('/').pop()), quantity: line.quantity,
            properties: Object.fromEntries(line.attributes.map(a => [a.key, a.value])) }));
          const expected = quantities(lines.map(line => ({ id: Number(line.merchandiseId.split('/').pop()), quantity: line.quantity,
            properties: Object.fromEntries(line.attributes.map(a => [a.key, a.value])) })));
          const received = quantities(mapped);
          if (expected.size !== received.size || [...expected].some(([key, quantity]) => received.get(key) !== quantity)) throw Error('quote-shape');
          let primaryTotal = 0;
          for (const line of actual.nodes) {
            const productId = Number(line.merchandise.product.id.split('/').pop());
            const key = ['primary', 'secondary'].find(key => this.config.products[key].id === productId);
            if (!key) continue;
            const variant = this.config.products[key].variants.find(v => v.id === Number(line.merchandise.id.split('/').pop()));
            if (!variant || money(line.cost.subtotalAmount, this.config.currency) !== variant.price * line.quantity) throw Error('price-changed');
            if (key === 'primary') primaryTotal += money(line.cost.totalAmount, this.config.currency);
          }
          const quoted = { items: actual.nodes.map(line => ({ id: Number(line.merchandise.id.split('/').pop()),
            product_id: Number(line.merchandise.product.id.split('/').pop()), quantity: line.quantity,
            properties: Object.fromEntries(line.attributes.map(a => [a.key, a.value])),
            original_line_price: money(line.cost.subtotalAmount, this.config.currency), final_line_price: money(line.cost.totalAmount, this.config.currency),
            line_level_discount_allocations: line.discountAllocations.map(a => ({ amount: money(a.discountedAmount, this.config.currency),
              discount_application: { title: a.__typename === 'CartAutomaticDiscountAllocation' ? a.title : null } })) })) };
          if (!quoted.items.some(i => i.properties[marker] === this.config.handle)) throw Error('gift-not-free');
          return { primaryTotal, balance: giftBalance(quoted, this.config) };
        } finally {
          if (created) {
            const { cartLinesRemove: cleared } = await this.storefront(clearQuery, {
              country: this.config.country, cart: created.id, lines: created.lines,
            });
            if (!Array.isArray(cleared?.userErrors) || cleared.userErrors.length || cleared.cart?.totalQuantity !== 0) throw Error('quote-cleanup');
            pendingQuotes.delete(created.id);
          }
        }
      }
    };
    api.quote = quoteEngine.quote;
    api.storefront = quoteEngine.storefront;
    api.activeRules = activeRules;
    api.offers = offers;
    api.refreshOffers = refreshOffers;
    api.blockCheckout = blockCheckout;

    // Financial reference only: unrelated server attributes and notes do not alter a gift's proof.
    const stamp = cart => JSON.stringify([cart.currency, cart.total_price, cart.discount_codes || [], cart.cart_level_discount_applications || [],
      cart.items.map(i => [identity(i), i.key, i.product_id, i.quantity,
      i.original_line_price, i.final_line_price, i.line_level_discount_allocations || [],
      i.parent_relationship || null, i.item_components || [], Boolean(i.has_components)]).sort()]);
    const confirmCleanup = (before, after, change) => {
      validateCart(after, before.currency);
      const targets = before.items.filter(i => i.key === change.id);
      const target = targets[0];
      if (targets.length !== 1 || !target.properties?.[marker] || target.selling_plan_allocation
        || target.parent_relationship || target.has_components || target.item_components?.length
        || !Number.isSafeInteger(change.quantity) || change.quantity < 0 || change.quantity >= target.quantity) throw Error('cleanup-identity');
      // Keys and allocations can change when Shopify reprices a gift. Compare all
      // choices, including ordinary purchases, rather than accepting a missing key.
      const choices = items => {
        const result = new Map();
        for (const i of items) {
          const key = JSON.stringify([i.product_id, i.id, Object.entries(i.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
            i.selling_plan_allocation || null, i.parent_relationship || null, i.item_components || [], Boolean(i.has_components)]);
          result.set(key, (result.get(key) || 0) + i.quantity);
        }
        return result;
      };
      const expected = choices(before.items.flatMap(i => i === target
        ? (change.quantity ? [{ ...i, quantity: change.quantity }] : []) : [i]));
      const actual = choices(after.items);
      if (expected.size !== actual.size || [...expected].some(([key, quantity]) => actual.get(key) !== quantity)) throw Error('cleanup-uncertain');
    };
    api.verifyCart = async (cart, rules) => {
      const reference = stamp(cart);
      verifyOwned(cart, rules);
      const owned = rules.filter(rule => cart.items.some(i => i.properties?.[marker] === rule.handle));
      for (const rule of owned) {
        // Ajax may omit the allocation's type. Cross its amounts with Shopify's
        // explicitly typed automatic allocation; a matching title alone is insufficient.
        const quote = await api.quote.call({ config: rule, storefront: api.storefront }, cart.items);
        if (JSON.stringify(quote.balance) !== JSON.stringify(giftBalance(cart, rule))) throw Error('gift-quote-changed');
      }
      if (owned.length) {
        const current = await Base.json(owned[0].cartUrl);
        if (!current.response.ok || stamp(validateCart(current.data, owned[0].currency)) !== reference) throw Error('gift-cart-changed');
      }
      return true;
    };

    class GiftCart extends HTMLElement {
      connectedCallback() {
        this.rules = [];
        try {
          this.config = JSON.parse(this.querySelector('[data-gift-cart-config]').textContent);
          this.rules = this.config.rules.map(validateRule);
          if (this.dataset.hasOwned === 'true') { api.hasOwned = true; blockCheckout(true); }
          void this.reconcile();
        } catch { if (this.dataset.hasOwned === 'true') this.fail(); }
      }
      fail() {
        blockCheckout(true);
        const status = this.querySelector('[data-gift-cart-status]');
        if (status) { status.textContent = this.config?.text.cleanupError || status.textContent; status.hidden = false; }
        const reload = this.querySelector('[data-gift-cart-reload]');
        if (reload) reload.hidden = false;
      }
      async reconcile() {
        // An old empty section must never release another verification's checkout hold.
        if (this.dataset.hasOwned !== 'true' && !api.checkoutBlocked) return;
        let acquired = false, changed = false;
        try {
          for (let n = 0; state.pending || Base.cartIsBusy(); n++) {
            if (!this.isConnected) return;
            if (n >= 200) throw Error('busy');
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          if (!this.isConnected) return;
          state.pending = true; state.version++; acquired = true;
          blockCheckout(true);
          refreshOffers();
          let current = await Base.json(this.config.cartUrl);
          if (!this.isConnected) return;
          if (!current.response.ok || !Array.isArray(current.data.items)) throw Error('cart');
          let cart = validateCart(current.data, this.rules[0]?.currency);
          const maximum = cart.items.length * 2 + 1;
          for (let n = 0; n < maximum; n++) {
            const change = cleanupPlan(cart, this.rules)[0];
            if (!change) {
              api.hasOwned = cart.items.some(i => i.properties?.[marker]);
              if (changed) {
                if (document.querySelector('cart-items')) { location.reload(); return; }
                const rendered = await Base.prototype.redraw.call({ button: document.querySelector('#CartDrawer-Checkout') }, cart, null);
                if (!this.isConnected) return; // Its replacement must verify its own live section.
                if (!rendered) { location.reload(); return; }
              }
              // A marker identifies a choice. Shopify may put its free allocation on
              // an ordinary line; verify the whole gift group before releasing checkout.
              await api.verifyCart(cart, this.rules);
              if (!this.isConnected) return;
              // verifyCart has no quote/read when the last gift is gone. Confirm
              // that rendering and asynchronous checks did not supersede this cart.
              const latest = await Base.json(this.config.cartUrl);
              if (!this.isConnected) return;
              if (!latest.response.ok || stamp(validateCart(latest.data, cart.currency)) !== stamp(cart)) throw Error('cleanup-cart-changed');
              // A navigation can render before another request commits. A fresh
              // API cart must not unlock a full-cart page still showing its gift.
              if (document.querySelector('cart-items') && (this.config.itemCount !== cart.item_count
                || (this.dataset.hasOwned === 'true') !== api.hasOwned)) throw Error('cart-render-changed');
              for (const selector of ['[data-gift-cart-status]', '[data-gift-cart-reload]']) {
                const node = this.querySelector(selector); if (node) node.hidden = true;
              }
              blockCheckout(false, cart.item_count);
              return;
            }
            const result = await Base.json(this.config.changeUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(change) });
            if (!this.isConnected) return;
            if (!result.response.ok && result.response.status !== 400) throw Error('cleanup');
            current = await Base.json(this.config.cartUrl);
            if (!this.isConnected) return;
            if (!current.response.ok) throw Error('cleanup');
            confirmCleanup(cart, current.data, change);
            // A 400 may refer to a line already removed. Accept only the exact
            // intended result with no cleanup left; never repeat a rejected POST.
            if (!result.response.ok && cleanupPlan(current.data, this.rules).length) throw Error('cleanup');
            cart = current.data; changed = true;
          }
          throw Error('cleanup-limit');
        } catch { if (this.isConnected) this.fail(); }
        finally {
          if (acquired) { state.pending = false; state.version++; refreshOffers(); }
        }
      }
    }
    customElements.define('k9-gift-cart', GiftCart);
    document.addEventListener('click', event => {
      // Managed offers acquire the shared lock and hold gifts immediately before POST.
      // Holding here would make Bogo/Volume reject their own click before revalidation.
      if (!event.target.closest('k9-gift-offer,k9-volume-offer,k9-paliers-offer,k9-bogo-offer')
        && event.target.closest('cart-remove-button, .quantity__button, gp-product-button, button[name="add"], [data-submit]')) api.holdMutation();
      if (event.target.closest('.k9-cart-toast__undo')) {
        api.holdMutation(Boolean(document.querySelector('cart-drawer')?.undoLine?.properties?.[marker]));
      }
      if (api.checkoutBlocked && event.target.closest('#CartDrawer-Checkout, #checkout, [name="checkout"], a[href^="/checkout"]')) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
    }, true);
    document.addEventListener('change', event => {
      if (event.target.matches('.quantity__input[data-index]')) api.holdMutation();
    }, true);
    document.addEventListener('submit', event => {
      if (api.checkoutBlocked && (event.submitter?.matches('[name="checkout"], #checkout, #CartDrawer-Checkout') || event.target.matches('form[action*="/checkout"]'))) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
    }, true);
  }
  if (window.K9NativeOffer) register();
  else document.addEventListener('DOMContentLoaded', register, { once: true });
})();
